import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, MessageSquare, LogOut, Globe, Calendar, Trash2, Settings, User, CreditCard, Code, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [newClient, setNewClient] = useState({ name: '', domain: '' });
  const [creating, setCreating] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchCurrentUser();
    fetchClients();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      setCurrentUser(user);
    } catch (error) {
      console.error('Failed to load user');
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async () => {
    // Prevent duplicate submissions
    if (creating) return;
    
    // Validate input
    if (!newClient.name || !newClient.name.trim()) {
      toast.error('Please enter a client name');
      return;
    }
    
    // Check for duplicate names
    const duplicateName = clients.some(c => c.name.toLowerCase() === newClient.name.trim().toLowerCase());
    if (duplicateName) {
      toast.error('A client with this name already exists');
      return;
    }
    
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/clients`, newClient, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Client created successfully');
      setDialogOpen(false);
      setNewClient({ name: '', domain: '' });
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteClient = async (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (!client) return;
    
    setClientToDelete(client);
    setDeleteConfirmName('');
    setDeleteDialogOpen(true);
  };

  const confirmDeleteClient = async () => {
    if (!clientToDelete) return;
    
    if (deleteConfirmName !== clientToDelete.name) {
      toast.error('Client name does not match');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/clients/${clientToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Client deleted');
      setDeleteDialogOpen(false);
      setClientToDelete(null);
      setDeleteConfirmName('');
      fetchClients();
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }} data-testid="dashboard-title">
              IcebreakChat
            </h1>
            <p className="text-gray-600">Manage your AI-powered chatbots</p>
          </div>
          <div className="flex gap-2 items-center">
            {currentUser?.is_super_admin && (
              <Button variant="outline" onClick={() => navigate('/super-admin')} className="bg-yellow-50">
                <Settings className="w-4 h-4 mr-2" /> Super Admin
              </Button>
            )}
            
            {/* User Dropdown Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center">
                    {currentUser?.username?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <span className="hidden md:inline">{currentUser?.username || 'User'}</span>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/plan-billing')}>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Plan & Billing
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="glass">
            <CardHeader>
              <CardDescription>Total Clients</CardDescription>
              <CardTitle className="text-3xl" data-testid="total-clients">{clients.length}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="glass">
            <CardHeader>
              <CardDescription>Active Chatbots</CardDescription>
              <CardTitle className="text-3xl">{clients.length}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="glass">
            <CardHeader>
              <CardDescription>Status</CardDescription>
              <CardTitle className="text-3xl text-green-600">All Active</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Clients Grid */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Your Clients</h2>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-client-button">
                <Plus className="w-4 h-4 mr-2" /> Add Client
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="add-client-dialog">
              <DialogHeader>
                <DialogTitle>Add New Client</DialogTitle>
                <DialogDescription>Create a new chatbot for your website</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Client Name</Label>
                  <Input
                    id="name"
                    data-testid="client-name-input"
                    placeholder="My Store"
                    value={newClient.name}
                    onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="domain">Website Domain</Label>
                  <Input
                    id="domain"
                    data-testid="client-domain-input"
                    placeholder="mystore.com"
                    value={newClient.domain}
                    onChange={(e) => setNewClient({ ...newClient, domain: e.target.value })}
                  />
                </div>
                <Button 
                  onClick={handleCreateClient} 
                  className="w-full" 
                  data-testid="create-client-button"
                  disabled={creating || !newClient.name.trim()}
                >
                  {creating ? 'Creating...' : 'Create Client'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-12" data-testid="loading-spinner">Loading...</div>
        ) : clients.length === 0 ? (
          <Card className="glass text-center py-12" data-testid="no-clients-message">
            <CardContent>
              <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-xl font-semibold mb-2">No clients yet</h3>
              <p className="text-gray-600 mb-4">Create your first chatbot to get started</p>
              <Button onClick={() => setDialogOpen(true)} data-testid="create-first-client-button">
                <Plus className="w-4 h-4 mr-2" /> Create Your First Client
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {clients.map((client) => (
              <Card key={client.id} className="glass hover:shadow-lg transition-all duration-300 cursor-pointer" data-testid={`client-card-${client.id}`}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1" onClick={() => navigate(`/client/${client.client_number}`)}>
                      <CardTitle className="mb-2">{client.name}</CardTitle>
                      <CardDescription className="flex items-center">
                        <Globe className="w-3 h-3 mr-1" /> {client.domain}
                      </CardDescription>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClient(client.id);
                      }}
                      data-testid={`delete-client-${client.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent onClick={() => navigate(`/client/${client.client_number}`)}>
                  <div className="flex items-center text-sm text-gray-500">
                    <Calendar className="w-3 h-3 mr-1" />
                    Created {new Date(client.created_at).toLocaleDateString()}
                  </div>
                  <Button className="w-full mt-4" variant="outline" data-testid={`manage-client-${client.id}`}>
                    Manage Chatbot
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Client</DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the client and all associated data including knowledge base, conversations, and settings.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <p className="text-sm font-medium">
                To confirm, type the client name: <span className="font-bold text-red-600">{clientToDelete?.name}</span>
              </p>
              <Input
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                placeholder="Enter client name"
                className="w-full"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setClientToDelete(null);
                  setDeleteConfirmName('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDeleteClient}
                disabled={deleteConfirmName !== clientToDelete?.name}
              >
                Delete Client
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
